import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { AuthService } from '../../features/auth/services/auth.service';
import { HttpService } from '../services/http.service';

/** Quién tiene la tablet de taller. Lo que el servidor sabe del mecánico que puso su PIN. */
export interface Operador {
  trabajadorId: number;
  nombre: string;
  cargo: string | null;
  /** Entró con el PIN temporal: antes de trabajar tiene que elegir el suyo. */
  debeCambiarPin: boolean;
}

/** Lo que el servidor responde al entrar. El token viaja una sola vez. */
interface EntradaServidor extends Operador {
  token: string;
}

/** Qué pasó al poner el PIN. */
export type ResultadoEntrada =
  | { ok: true }
  /** `intentosRestantes`: cuántos PIN más antes del bloqueo; null si el servidor no lo dice (versión anterior). */
  | { ok: false; motivo: 'incorrecto'; intentosRestantes: number | null }
  | { ok: false; motivo: 'bloqueada'; segundos: number }
  | { ok: false; motivo: 'sin-conexion' }
  | { ok: false; motivo: 'error' };

export type ResultadoNuevoPin = 'ok' | 'facil' | 'no-disponible' | 'sin-conexion' | 'error';

/** Los códigos con que el servidor dice que la sesión de operador no sirve (OperadorFilter). */
export const CODIGO_OPERADOR_VENCIDO = 'OPERADOR_VENCIDO';
export const CODIGO_OPERADOR_INVALIDO = 'OPERADOR_INVALIDO';
export const CODIGO_OPERADOR_PIN_TEMPORAL = 'OPERADOR_PIN_TEMPORAL';

export const ENCABEZADO_OPERADOR = 'X-Operador';

/** Un entero mayor que cero, o null si no lo es (lo que llega del servidor no se da por bueno sin mirarlo). */
function numeroPositivo(valor: unknown): number | null {
  return typeof valor === 'number' && Number.isInteger(valor) && valor > 0 ? valor : null;
}

/** El código de operador de un error HTTP, o null si el error es de otra cosa. */
export function codigoDeOperador(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 403) return null;
  const codigo = (error.error as { code?: unknown } | null)?.code;
  return typeof codigo === 'string' && codigo.startsWith('OPERADOR_') ? codigo : null;
}

/** Lo que dice `GET /taller/operador/estado`. */
interface EstadoServidor {
  habilitado?: boolean;
  requierePin?: boolean;
  /** Cuánto le falta a esta tablet para desbloquearse sola; 0 si no está bloqueada. Un servidor anterior no lo manda. */
  bloqueadaSegundos?: number;
}

interface Guardado {
  usuarioId: number;
  token: string;
  trabajadorId: number;
  nombre: string;
  cargo: string | null;
}

const CLAVE = 'softrent.operador.v1';
const RUTA = '/taller/operador';
/**
 * El servidor bloquea a los 30 minutos sin usar la tablet (`app.pin.inactividad-minutos`); la pantalla avisa un minuto
 * antes. Eran 15 y pedían el PIN en cada pausa del taller (Samuel, 18-oct).
 */
const INACTIVIDAD_MS = 30 * 60_000;
const AVISO_MS = 29 * 60_000;
/** Mientras se está usando la tablet se le avisa al servidor cada tanto, para que no la bloquee por su lado. */
const LATIDO_MS = 4 * 60_000;
const REVISION_MS = 15_000;
const BLOQUEO_POR_DEFECTO_S = 60;

/**
 * Quién tiene la tablet de taller (mecánico por PIN).
 *
 * <p>La tablet queda abierta con una cuenta de taller compartida; este servicio sabe qué mecánico la está usando. Sin PIN activado
 * en la empresa, o sin ningún mecánico con PIN, <b>no hace nada</b>: el taller funciona como siempre y las acciones quedan a nombre
 * de la cuenta.
 *
 * <p>El token de la sesión de operador se guarda en la tablet y viaja en {@code X-Operador} (ver el interceptor). El servidor lo
 * vence a los 30 minutos sin uso; aquí se refleja con un bloqueo de pantalla (aviso al minuto 29) que no cierra la sesión del
 * servidor: si el mecánico vuelve a poner su PIN, sigue donde estaba.
 */
@Injectable({ providedIn: 'root' })
export class OperadorService {
  private readonly http = inject(HttpService);
  private readonly auth = inject(AuthService);

  private readonly _habilitado = signal(false);
  private readonly _requierePin = signal(false);
  private readonly _operador = signal<Operador | null>(null);
  private readonly _pendiente = signal<EntradaServidor | null>(null);
  private readonly _bloqueado = signal(false);
  private readonly _aviso = signal(false);
  private readonly _bloqueadaHastaMs = signal<number | null>(null);
  /** Ya se sabe si esta tablet pide PIN (hasta entonces no se manda lo que quedó esperando a un mecánico). */
  private readonly _decidido = signal(false);

  private token: string | null = null;
  private iniciado = false;
  private temporizador: ReturnType<typeof setInterval> | null = null;
  private ultimaActividad = Date.now();
  private ultimoLatido = Date.now();
  private readonly _desbloqueos = new Subject<number | null>();

  /** El acceso por PIN está activado en esta instalación. */
  readonly habilitado = this._habilitado.asReadonly();
  /** El mecánico que tiene la tablet, ya confirmado («Soy yo»). */
  readonly operador = this._operador.asReadonly();
  /** Alguien puso su PIN y falta que confirme que es él. */
  readonly pendiente = this._pendiente.asReadonly();
  /** Pasó un rato sin usar la tablet: la pantalla pide el PIN de nuevo. */
  readonly bloqueado = this._bloqueado.asReadonly();
  /** Falta un minuto para el bloqueo. */
  readonly avisoDeBloqueo = this._aviso.asReadonly();
  /** Hasta cuándo no se deja probar otro PIN (tras demasiados intentos). */
  readonly bloqueadaHastaMs = this._bloqueadaHastaMs.asReadonly();

  /** Esta tablet pide PIN: es una cuenta de técnico, el acceso está activado y hay mecánicos con PIN. */
  readonly activa = computed(() => this._requierePin() && this.auth.isTecnico());
  /** Hay que mostrar la pantalla del PIN encima del trabajo. */
  readonly compuertaAbierta = computed(() => {
    if (!this.activa()) return false;
    const o = this._operador();
    return o === null || this._bloqueado() || o.debeCambiarPin;
  });
  /** Quien está listo para trabajar. */
  readonly listo = computed(() => this.activa() && !this.compuertaAbierta() && this._operador() !== null);

  /**
   * A nombre de quién va lo que se haga en la tablet, para las pantallas que muestran «lo suyo» (la portada del mecánico):
   * {@code 'cuenta'} si va a nombre de la cuenta (no es técnico, o la tablet no pide PIN), {@code 'op:<id>'} con el mecánico
   * listo, y {@code null} mientras no se sabe: todavía no se preguntó si se pide PIN, nadie lo puso, o la tablet está bloqueada.
   *
   * <p>Existe por el 23-oct: la portada pedía la jornada al abrirse, antes del PIN, y se quedaba con «Tablet del taller» aunque
   * después Antonio confirmara «Soy yo». Quien muestre algo de una persona tiene que esperar a saber quién es.
   */
  readonly quien = computed<string | null>(() => {
    // El signal primero: `isTecnico()` no lo es, y si el computed saliera sin leer ninguno no se recalcularía nunca, ni
    // en la sesión siguiente de la misma pestaña.
    const decidido = this._decidido();
    if (!this.auth.isTecnico()) return 'cuenta';
    if (!decidido) return null;
    if (!this.activa()) return 'cuenta';
    const o = this._operador();
    return this.listo() && o ? `op:${o.trabajadorId}` : null;
  });

  /** Emite el id del mecánico cada vez que la tablet queda lista para trabajar; null si se cierra la sesión de la cuenta. */
  readonly desbloqueos$: Observable<number | null> = this._desbloqueos.asObservable();

  constructor() {
    const destroyRef = inject(DestroyRef);
    // Otra cuenta, otro mecánico: nada del anterior se hereda.
    this.auth.currentUser$.pipe(takeUntilDestroyed(destroyRef)).subscribe(usuario => {
      if (!usuario) this.olvidarTodo();
    });
    destroyRef.onDestroy(() => this.detener());
  }

  // ── Arranque ────────────────────────────────────────────────────────────

  /**
   * Pregunta al servidor si hay que pedir el PIN y, si había un mecánico en la tablet, comprueba que su sesión siga viva. Se llama
   * una vez, al abrir el taller. Sin red no pide nada: el mecánico que ya estaba sigue.
   */
  async iniciar(): Promise<void> {
    if (!this.auth.isTecnico()) return;
    if (this.iniciado) return;
    this.iniciado = true;
    this.recuperar();
    try {
      const respuesta = await firstValueFrom(this.http.sondear(`${RUTA}/estado`)) as { data?: EstadoServidor };
      this._habilitado.set(!!respuesta?.data?.habilitado);
      this._requierePin.set(!!respuesta?.data?.requierePin);
      // Si la tablet ya venía bloqueada (se recargó durante el bloqueo), la pantalla lo muestra sin esperar a que alguien pruebe.
      this.anotarBloqueo(respuesta?.data?.bloqueadaSegundos);
    } catch {
      // Sin respuesta: si ya había un mecánico en la tablet se le deja seguir; si no, nada que pedir.
      this._requierePin.set(this._operador() !== null);
      this.iniciado = false;
    }
    this._decidido.set(true);
    if (!this._requierePin()) {
      this.olvidarOperador();
      this._desbloqueos.next(null);
      return;
    }
    this.arrancarReloj();
    if (this._operador()) await this.comprobarSesion();
  }

  /** La tablet vuelve a preguntar (por ejemplo al volver a abrir la página tras un rato). */
  reiniciar(): Promise<void> {
    this.iniciado = false;
    return this.iniciar();
  }

  private recuperar(): void {
    const guardado = this.leer();
    if (!guardado || guardado.usuarioId !== this.auth.getUserId()) {
      this.borrar();
      return;
    }
    this.token = guardado.token;
    this._operador.set({ trabajadorId: guardado.trabajadorId, nombre: guardado.nombre, cargo: guardado.cargo, debeCambiarPin: false });
  }

  private async comprobarSesion(): Promise<void> {
    try {
      const r = await firstValueFrom(this.http.sondear(`${RUTA}/yo`)) as { data?: Operador };
      if (r?.data?.trabajadorId) this._operador.set({ ...r.data, cargo: r.data.cargo ?? null });
      this.ultimaActividad = Date.now();
      this.ultimoLatido = Date.now();
      // Si mientras tanto el servidor ya dijo que la sesión venció (un pedido recibió OPERADOR_VENCIDO), no está lista: avisarlo
      // haría repetir ese pedido sin que nadie haya puesto el PIN.
      const o = this._operador();
      if (o && !o.debeCambiarPin && !this._bloqueado()) this._desbloqueos.next(o.trabajadorId);
    } catch (e) {
      // 403 con código: el interceptor ya bloqueó o soltó la sesión. Sin red: se queda como está.
      if (e instanceof HttpErrorResponse && e.status !== 0 && !codigoDeOperador(e)) this.olvidarOperador();
    }
  }

  // ── Lo que ven los demás ────────────────────────────────────────────────

  /** El token para el encabezado {@code X-Operador}; null si no hay mecánico. */
  tokenActual(): string | null {
    return this.token;
  }

  /** El mecánico que tiene la tablet, tal como lo vio el último envío (para atar un pedido a su dueño). */
  idActual(): number | null {
    return this._operador()?.trabajadorId ?? null;
  }

  /**
   * Ya se sabe que esta tablet no pide PIN (el acceso está apagado o ningún mecánico lo tiene): lo que se haga va a nombre de la
   * cuenta, como siempre. Antes de preguntarle al servidor, o sin sesión de la cuenta, no se sabe.
   */
  sinPin(): boolean {
    return this._decidido() && !this.activa();
  }

  /**
   * ¿Se puede mandar ahora algo que hizo este mecánico? Lo que se guardó sin señal solo sale cuando la tablet la tiene de nuevo el
   * mismo mecánico que lo hizo: si no, quedaría a nombre de otro.
   */
  puedeEnviar(operadorIdDeLaOperacion: number | null | undefined): boolean {
    if (operadorIdDeLaOperacion == null) return true;
    if (!this._decidido()) return false;
    if (!this.activa()) return true;
    const o = this._operador();
    return o !== null && !this._bloqueado() && !o.debeCambiarPin && o.trabajadorId === operadorIdDeLaOperacion;
  }

  /** Espera a que la tablet quede lista; devuelve el mecánico que la tiene (o null si se cerró la sesión). */
  esperarDesbloqueo(): Observable<number | null> {
    return new Observable<number | null>(suscriptor => {
      const sub = this._desbloqueos.subscribe(id => { suscriptor.next(id); suscriptor.complete(); });
      return () => sub.unsubscribe();
    });
  }

  // ── Lo que dice el servidor (interceptor) ───────────────────────────────

  /** El servidor vio la sesión vencida por inactividad. */
  alVencer(): void {
    if (this._operador()) this._bloqueado.set(true);
  }

  /** La sesión ya no existe (cerrada, o el mecánico dejó de tener acceso). */
  alInvalidarse(): void {
    this.olvidarOperador();
  }

  /** Entró con el PIN temporal y todavía tiene que elegir el suyo. */
  alExigirCambioDePin(): void {
    const o = this._operador();
    if (o) this._operador.set({ ...o, debeCambiarPin: true });
  }

  // ── Entrar, confirmar, elegir PIN, cambiar ──────────────────────────────

  /** Pone el PIN. Si vale, queda pendiente de que el mecánico confirme que es él. */
  async entrar(pin: string): Promise<ResultadoEntrada> {
    try {
      const r = await firstValueFrom(this.http.enviarEnSegundoPlano('POST', `${RUTA}/entrar`, { pin }));
      const d = (r.body as { data?: Partial<EntradaServidor> } | null)?.data;
      if (!d?.token || typeof d.trabajadorId !== 'number' || !d.nombre) return { ok: false, motivo: 'error' };
      this._pendiente.set({
        token: d.token, trabajadorId: d.trabajadorId, nombre: d.nombre, cargo: d.cargo ?? null,
        debeCambiarPin: !!d.debeCambiarPin,
      });
      this._bloqueadaHastaMs.set(null);
      return { ok: true };
    } catch (e) {
      return this.explicarFallo(e);
    }
  }

  /** «Soy yo»: la tablet pasa a ser de este mecánico. */
  async confirmar(): Promise<void> {
    const entrada = this._pendiente();
    if (!entrada) return;
    const anterior = this.token;
    this._pendiente.set(null);
    this.token = entrada.token;
    this._operador.set({
      trabajadorId: entrada.trabajadorId, nombre: entrada.nombre, cargo: entrada.cargo, debeCambiarPin: entrada.debeCambiarPin,
    });
    this._bloqueado.set(false);
    this._aviso.set(false);
    this.ultimaActividad = Date.now();
    this.ultimoLatido = Date.now();
    this.guardar();
    // La sesión del que tenía la tablet ya no hace falta: se cierra para que no quede abierta en nombre de nadie.
    if (anterior && anterior !== entrada.token) void this.cerrarEnServidor(anterior);
    if (!entrada.debeCambiarPin) this._desbloqueos.next(entrada.trabajadorId);
  }

  /** «No soy yo»: se cierra la sesión recién abierta y se vuelve a pedir el PIN. */
  async rechazar(): Promise<void> {
    const entrada = this._pendiente();
    this._pendiente.set(null);
    if (entrada) await this.cerrarEnServidor(entrada.token);
  }

  /** Elige el PIN propio (obligatorio tras entrar con el temporal). */
  async elegirPin(pinNuevo: string): Promise<ResultadoNuevoPin> {
    const o = this._operador();
    if (!o) return 'error';
    try {
      await firstValueFrom(this.http.enviarEnSegundoPlano('POST', `${RUTA}/cambiar-pin`, { pinNuevo }));
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        if (e.status === 0) return 'sin-conexion';
        const mensaje = String((e.error as { message?: unknown } | null)?.message ?? '');
        if (/f[aá]cil/i.test(mensaje)) return 'facil';
        if (/disponible/i.test(mensaje)) return 'no-disponible';
      }
      return 'error';
    }
    this._operador.set({ ...o, debeCambiarPin: false });
    this.ultimaActividad = Date.now();
    this._desbloqueos.next(o.trabajadorId);
    return 'ok';
  }

  /** «Cambiar»: el mecánico deja la tablet. Lo que llevaba se queda guardado. */
  async cambiar(): Promise<void> {
    const token = this.token;
    this.olvidarOperador();
    if (token) await this.cerrarEnServidor(token);
  }

  /**
   * Mientras la tablet está bloqueada por intentos, la pantalla pregunta cada tanto si sigue así: administración la puede
   * desbloquear desde Trabajadores sin esperar los 5 minutos. Sin señal, o con un servidor que no lo informa, sigue la cuenta.
   */
  async revisarBloqueo(): Promise<void> {
    try {
      const r = await firstValueFrom(this.http.sondear(`${RUTA}/estado`)) as { data?: EstadoServidor };
      this.anotarBloqueo(r?.data?.bloqueadaSegundos);
    } catch {
      // Sin señal: la cuenta regresiva sigue con lo que se sabía.
    }
  }

  /** «Seguir trabajando» tras el aviso: cuenta como actividad y le avisa al servidor. */
  async seguir(): Promise<void> {
    this.registrarActividad();
    await this.latido();
  }

  /** Un toque o tecla en la tablet: el mecánico sigue ahí. */
  registrarActividad(): void {
    if (this._bloqueado()) return;
    this.ultimaActividad = Date.now();
    this._aviso.set(false);
  }

  // ── Reloj de inactividad ────────────────────────────────────────────────

  private arrancarReloj(): void {
    if (this.temporizador || typeof window === 'undefined') return;
    const alActividad = () => this.registrarActividad();
    for (const evento of ['pointerdown', 'keydown', 'touchstart', 'wheel']) {
      document.addEventListener(evento, alActividad, { passive: true, capture: true });
    }
    this.quitarOyentes = () => {
      for (const evento of ['pointerdown', 'keydown', 'touchstart', 'wheel']) {
        document.removeEventListener(evento, alActividad, { capture: true });
      }
    };
    this.temporizador = setInterval(() => this.revisar(), REVISION_MS);
  }

  private quitarOyentes: () => void = () => undefined;

  /** Visible para las pruebas: un paso del reloj. */
  revisar(ahora: number = Date.now()): void {
    if (!this._operador() || !this.activa()) return;
    if (this._bloqueado() || this._operador()!.debeCambiarPin) return;
    const quieto = ahora - this.ultimaActividad;
    // Sin señal no se bloquea: pedir el PIN sin poder validarlo dejaría al mecánico afuera de su trabajo.
    const enLinea = typeof navigator === 'undefined' || navigator.onLine;
    if (quieto >= INACTIVIDAD_MS && enLinea) {
      this._bloqueado.set(true);
      this._aviso.set(false);
      return;
    }
    if (quieto >= AVISO_MS) {
      this._aviso.set(true);
      return;
    }
    if (quieto < LATIDO_MS && ahora - this.ultimoLatido >= LATIDO_MS && enLinea) void this.latido();
  }

  private async latido(): Promise<void> {
    this.ultimoLatido = Date.now();
    try {
      await firstValueFrom(this.http.sondear(`${RUTA}/yo`));
    } catch {
      // 403 con código: el interceptor ya lo tomó. Sin red: no pasa nada.
    }
  }

  // ── Internos ────────────────────────────────────────────────────────────

  private explicarFallo(e: unknown): ResultadoEntrada {
    if (!(e instanceof HttpErrorResponse)) return { ok: false, motivo: 'error' };
    if (e.status === 0) return { ok: false, motivo: 'sin-conexion' };
    const cuerpo = e.error as { message?: unknown; detail?: unknown; data?: { intentosRestantes?: unknown; segundosBloqueo?: unknown } } | null;
    const mensaje = String(cuerpo?.message ?? cuerpo?.detail ?? '');
    // Los números vienen aparte (PinRechazadoException); un servidor anterior solo los tenía en el texto, o no los tenía.
    const segundosDelServidor = numeroPositivo(cuerpo?.data?.segundosBloqueo);
    const espera = /(\d+)\s*min/i.exec(mensaje);
    if (e.status === 429 || segundosDelServidor !== null || /demasiados intentos/i.test(mensaje)) {
      const segundos = segundosDelServidor ?? (espera ? Number(espera[1]) * 60 : BLOQUEO_POR_DEFECTO_S);
      this._bloqueadaHastaMs.set(Date.now() + segundos * 1000);
      return { ok: false, motivo: 'bloqueada', segundos };
    }
    if (e.status === 400 && /incorrecto/i.test(mensaje)) {
      return { ok: false, motivo: 'incorrecto', intentosRestantes: numeroPositivo(cuerpo?.data?.intentosRestantes) };
    }
    return { ok: false, motivo: 'error' };
  }

  /** Lo que el servidor dice del bloqueo: si sigue, hasta cuándo; si ya no (o administración la desbloqueó), se suelta. */
  private anotarBloqueo(segundos: unknown): void {
    if (typeof segundos !== 'number') return;
    this._bloqueadaHastaMs.set(segundos > 0 ? Date.now() + segundos * 1000 : null);
  }

  private async cerrarEnServidor(token: string): Promise<void> {
    try {
      const sesion = this.http.enviarEnSegundoPlano('POST', `${RUTA}/salir`, {}, { [ENCABEZADO_OPERADOR]: token });
      await firstValueFrom(sesion);
    } catch {
      // Se cierra sola a los 30 minutos sin uso; no hay nada que decirle al mecánico.
    }
  }

  private olvidarOperador(borrarGuardado = true): void {
    this.token = null;
    this._operador.set(null);
    this._pendiente.set(null);
    this._bloqueado.set(false);
    this._aviso.set(false);
    if (borrarGuardado) this.borrar();
  }

  private olvidarTodo(): void {
    this.detener();
    // Lo guardado es de la sesión del panel (la cuenta del taller). Una pestaña del portal sin sesión también ve un usuario nulo,
    // pero es el de su zona: borrarlo ahí le volvía a pedir el PIN a la tablet de al lado (#746). Por eso se pregunta por la zona
    // explícita y no por la URL, que durante una navegación todavía es la de origen.
    this.olvidarOperador(!this.auth.haySesionEn('panel'));
    this._habilitado.set(false);
    this._requierePin.set(false);
    this._decidido.set(false);
    this._bloqueadaHastaMs.set(null);
    this.iniciado = false;
    this._desbloqueos.next(null);
  }

  private detener(): void {
    if (this.temporizador) clearInterval(this.temporizador);
    this.temporizador = null;
    this.quitarOyentes();
    this.quitarOyentes = () => undefined;
  }

  private guardar(): void {
    const o = this._operador();
    const usuarioId = this.auth.getUserId();
    if (!o || !this.token || usuarioId == null) return;
    const dato: Guardado = { usuarioId, token: this.token, trabajadorId: o.trabajadorId, nombre: o.nombre, cargo: o.cargo };
    try { localStorage.setItem(CLAVE, JSON.stringify(dato)); } catch { /* sin almacenamiento: queda en memoria */ }
  }

  private leer(): Guardado | null {
    try {
      const crudo = localStorage.getItem(CLAVE);
      const d = crudo ? JSON.parse(crudo) as Partial<Guardado> : null;
      return d && typeof d.token === 'string' && typeof d.trabajadorId === 'number' && typeof d.nombre === 'string'
        && typeof d.usuarioId === 'number' ? d as Guardado : null;
    } catch { return null; }
  }

  private borrar(): void {
    try { localStorage.removeItem(CLAVE); } catch { /* nada */ }
  }
}
